/**
 * Lenavio Succession — top-level admin page.
 *
 * Three tabs:
 *
 *   1. **Positions**  — grid of cards: title, current holder,
 *      criticality + risk badges, candidate count, top
 *      candidate readiness.
 *   2. **9-box grid** — performance × potential matrix of every
 *      candidate across all positions. Honest 3×3 plot — admins
 *      click a name to jump to that candidate's position detail.
 *   3. **Risks**      — at-risk positions surfaced from the
 *      dashboard endpoint with a reason blurb each.
 *
 * Position detail (and per-position candidate management) lives
 * at ``/succession/positions/{id}`` — this page is the rollup.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  Plus,
  Shield,
  Users,
} from "lucide-react";

import {
  createPosition,
  getDashboard,
  listPositions,
  type Criticality,
  type KeyPosition,
  type SuccessionDashboard,
} from "@/api/succession";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { Tabs } from "@/components/ui/Tabs";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/useToast";
import { pageTransition } from "@/lib/utils";

const CRITICALITY_OPTIONS: { value: Criticality; label: string }[] = [
  { value: "critical", label: "Critical" },
  { value: "important", label: "Important" },
  { value: "standard", label: "Standard" },
];

export function SuccessionPage() {
  const [tab, setTab] = useState<"positions" | "ninebox" | "risks">("positions");

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Succession"
        subtitle="Key positions, bench depth, and the 9-box."
      />

      <Tabs
        tabs={[
          { id: "positions", label: "Positions" },
          { id: "ninebox", label: "9-box grid" },
          { id: "risks", label: "Risks" },
        ]}
        activeTab={tab}
        onChange={(v) => setTab(v as "positions" | "ninebox" | "risks")}
        className="mb-6"
      />

      {tab === "positions" && <PositionsGrid />}
      {tab === "ninebox" && <NineBoxGrid />}
      {tab === "risks" && <RisksView />}
    </motion.div>
  );
}


// ════════════════════════════════════════════════════════════════════
// Positions grid
// ════════════════════════════════════════════════════════════════════


function PositionsGrid() {
  const { addToast } = useToast();
  const [rows, setRows] = useState<KeyPosition[] | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    try {
      setRows(await listPositions());
    } catch {
      addToast("error", "Could not load positions.");
    }
  };
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" />
          New key position
        </Button>
      </div>

      {rows === null ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-44 rounded bg-border/30 animate-pulse" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <Card className="text-center py-12">
          <Users className="h-8 w-8 text-text-muted mx-auto mb-2" />
          <p className="text-sm text-text-muted">
            No key positions yet. Add the first one to start succession planning.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((p) => (
            <Link
              key={p.id}
              to={`/succession/positions/${p.id}`}
              className="block group"
            >
              <Card className="h-full hover:border-accent/50 transition-colors">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-text-primary truncate">
                      {p.title}
                    </h3>
                    <p className="text-xs text-text-muted truncate">
                      {p.department || "—"}
                    </p>
                  </div>
                  <RiskDot risk={p.risk} />
                </div>
                <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                  <Badge
                    variant={
                      p.criticality === "critical"
                        ? "danger"
                        : p.criticality === "important"
                          ? "warning"
                          : "default"
                    }
                  >
                    {p.criticality}
                  </Badge>
                  <Badge variant="info">
                    {p.candidate_count} candidate
                    {p.candidate_count === 1 ? "" : "s"}
                  </Badge>
                </div>
                <p className="text-xs text-text-muted mb-1">
                  Current holder
                </p>
                <p className="text-sm text-text-primary mb-3">
                  {p.current_holder_name || (
                    <span className="text-danger">Vacant</span>
                  )}
                </p>
                <p className="text-xs text-text-muted">Top successor</p>
                <p className="text-sm text-text-primary">
                  {p.candidates.length === 0 ? (
                    <span className="text-text-muted italic">
                      No bench
                    </span>
                  ) : (
                    <>
                      {p.candidates[0].candidate_name}
                      <ReadinessPill readiness={p.candidates[0].readiness} />
                    </>
                  )}
                </p>
                <div className="mt-3 inline-flex items-center gap-1 text-xs text-accent group-hover:underline">
                  Manage bench <ArrowRight className="h-3 w-3" />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {creating && (
        <NewPositionModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void refresh();
          }}
        />
      )}
    </>
  );
}


function RiskDot({ risk }: { risk: "red" | "yellow" | "green" }) {
  const colors = {
    red: "bg-danger",
    yellow: "bg-amber-500",
    green: "bg-success",
  };
  const labels = {
    red: "High risk — no ready successor",
    yellow: "Watch — sub-threshold readiness",
    green: "Healthy — ready successor on bench",
  };
  return (
    <span
      className={`h-2.5 w-2.5 rounded-full ${colors[risk]} mt-1.5 shrink-0`}
      title={labels[risk]}
      aria-label={labels[risk]}
    />
  );
}


function ReadinessPill({
  readiness,
}: {
  readiness: "ready_now" | "ready_1yr" | "ready_2yr" | "development_needed";
}) {
  const colors = {
    ready_now: "bg-success/10 text-success",
    ready_1yr: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    ready_2yr: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    development_needed: "bg-danger/10 text-danger",
  };
  const labels = {
    ready_now: "Ready now",
    ready_1yr: "Ready ~1 yr",
    ready_2yr: "Ready ~2 yrs",
    development_needed: "Development needed",
  };
  return (
    <span
      className={`ml-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${colors[readiness]}`}
    >
      {labels[readiness]}
    </span>
  );
}


function NewPositionModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { addToast } = useToast();
  const [title, setTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [criticality, setCriticality] = useState<Criticality>("important");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const save = async () => {
    if (!title.trim()) {
      addToast("error", "Title is required.");
      return;
    }
    setSubmitting(true);
    try {
      await createPosition({
        title: title.trim(),
        department: department.trim() || undefined,
        criticality,
        notes: notes.trim() || undefined,
      });
      addToast("success", "Position added.");
      onCreated();
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not add position.";
      addToast("error", detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="New key position" size="md">
      <div className="space-y-3">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="VP Engineering"
        />
        <Input
          label="Department"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          placeholder="Engineering"
        />
        <Select
          label="Criticality"
          value={criticality}
          onChange={(e) => setCriticality(e.target.value as Criticality)}
          options={CRITICALITY_OPTIONS}
        />
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
            placeholder="What makes this role key? What's the risk if it's unfilled?"
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={save} isLoading={submitting}>
            Create
          </Button>
        </div>
      </div>
    </Modal>
  );
}


// ════════════════════════════════════════════════════════════════════
// 9-box grid (performance × potential)
// ════════════════════════════════════════════════════════════════════


function NineBoxGrid() {
  const { addToast } = useToast();
  const [rows, setRows] = useState<KeyPosition[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    listPositions()
      .then((rs) => {
        if (!cancelled) setRows(rs);
      })
      .catch(() => addToast("error", "Could not load 9-box."));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aggregate every candidate across every position. Cells indexed
  // by (perf, pot) where each is 1..3. Skip rows missing either
  // rating — the matrix only renders fully-rated candidates.
  const cells = useMemo(() => {
    const grid: Record<string, { name: string; positionId: string }[]> = {};
    if (!rows) return grid;
    for (const p of rows) {
      for (const c of p.candidates) {
        if (
          typeof c.performance_rating === "number" &&
          typeof c.potential_rating === "number"
        ) {
          const key = `${c.performance_rating}|${c.potential_rating}`;
          (grid[key] = grid[key] || []).push({
            name: c.candidate_name,
            positionId: p.id,
          });
        }
      }
    }
    return grid;
  }, [rows]);

  if (rows === null) {
    return <div className="h-96 rounded bg-border/30 animate-pulse" />;
  }

  // Cell colour by quadrant — deliberately understated. The plot
  // is read by row + column, not by cell colour.
  const cellColour = (perf: number, pot: number): string => {
    const sum = perf + pot;
    if (sum >= 5) return "bg-success/10 border-success/30";
    if (sum >= 4) return "bg-accent/5 border-accent/30";
    if (sum >= 3) return "bg-amber-500/10 border-amber-500/30";
    return "bg-danger/5 border-danger/30";
  };

  // Render rows top-to-bottom: pot=3 (high) at top.
  const potRows = [3, 2, 1];
  const perfCols = [1, 2, 3];

  return (
    <div className="space-y-3">
      <p className="text-sm text-text-muted">
        Drop candidates onto the 9-box by editing their performance &
        potential ratings on the position detail page. Both axes are
        1 (low) → 3 (high).
      </p>
      <div className="overflow-x-auto">
        <div className="inline-grid" style={{ gridTemplateColumns: "auto repeat(3, minmax(180px, 1fr))" }}>
          {/* Header row — performance axis */}
          <div />
          {perfCols.map((p) => (
            <div
              key={`hdr-${p}`}
              className="text-center text-xs font-medium uppercase tracking-wide text-text-muted py-2"
            >
              Perf {p}
            </div>
          ))}

          {potRows.map((pot) => (
            <>
              <div
                key={`pot-${pot}`}
                className="text-xs font-medium uppercase tracking-wide text-text-muted py-2 pr-3 self-center text-right"
              >
                Pot {pot}
              </div>
              {perfCols.map((perf) => {
                const items = cells[`${perf}|${pot}`] || [];
                return (
                  <div
                    key={`${perf}-${pot}`}
                    className={`m-1 min-h-[8rem] rounded-lg border p-2 text-xs ${cellColour(perf, pot)}`}
                  >
                    {items.length === 0 ? (
                      <span className="text-text-muted italic">—</span>
                    ) : (
                      <ul className="space-y-1">
                        {items.map((it, i) => (
                          <li key={i}>
                            <Link
                              to={`/succession/positions/${it.positionId}`}
                              className="text-text-primary hover:underline"
                            >
                              {it.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </>
          ))}
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════
// Risks view
// ════════════════════════════════════════════════════════════════════


function RisksView() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [data, setData] = useState<SuccessionDashboard | null>(null);

  useEffect(() => {
    let cancelled = false;
    getDashboard()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => addToast("error", "Could not load dashboard."));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (data === null) {
    return <div className="h-64 rounded bg-border/30 animate-pulse" />;
  }
  const coveragePct = Math.round(data.coverage_pct * 100);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card>
          <p className="text-xs uppercase tracking-wide text-text-muted">
            Coverage
          </p>
          <p className="text-2xl font-semibold font-heading text-text-primary">
            {coveragePct}%
          </p>
          <p className="text-xs text-text-muted mt-0.5">
            {data.positions_with_ready_now} of {data.total_positions} positions
            with a ready-now successor
          </p>
        </Card>
        <Card>
          <p className="text-xs uppercase tracking-wide text-text-muted">
            Risk roll-up
          </p>
          <p className="text-2xl font-semibold font-heading text-text-primary">
            {data.by_risk.red} <span className="text-text-muted text-sm font-normal">red</span>{" "}
            · {data.by_risk.yellow}{" "}
            <span className="text-text-muted text-sm font-normal">yellow</span>{" "}
            · {data.by_risk.green}{" "}
            <span className="text-text-muted text-sm font-normal">green</span>
          </p>
        </Card>
        <Card>
          <p className="text-xs uppercase tracking-wide text-text-muted">
            By criticality
          </p>
          <p className="text-2xl font-semibold font-heading text-text-primary">
            {data.by_criticality.critical}
            <span className="text-text-muted text-sm font-normal"> critical</span>
          </p>
          <p className="text-xs text-text-muted mt-0.5">
            {data.by_criticality.important} important ·{" "}
            {data.by_criticality.standard} standard
          </p>
        </Card>
      </div>

      {data.at_risk.length === 0 ? (
        <Card className="text-center py-10">
          <Shield className="h-8 w-8 text-success mx-auto mb-2" />
          <p className="text-sm text-text-primary font-medium">
            No at-risk positions
          </p>
          <p className="text-xs text-text-muted">
            Every key role has a ready-now successor.
          </p>
        </Card>
      ) : (
        <ul className="space-y-2">
          {data.at_risk.map((r) => (
            <li key={r.position_id}>
              <button
                type="button"
                onClick={() => navigate(`/succession/positions/${r.position_id}`)}
                className={`w-full text-left rounded-xl border p-4 transition-colors ${
                  r.risk === "red"
                    ? "border-danger/40 bg-danger/5 hover:bg-danger/10"
                    : "border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10"
                }`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="text-sm font-semibold text-text-primary">
                        {r.title}
                      </p>
                      <Badge
                        variant={
                          r.criticality === "critical"
                            ? "danger"
                            : r.criticality === "important"
                              ? "warning"
                              : "default"
                        }
                      >
                        {r.criticality}
                      </Badge>
                    </div>
                    <p className="text-xs text-text-muted">
                      {r.department || "—"} · {r.candidate_count} candidate
                      {r.candidate_count === 1 ? "" : "s"}
                    </p>
                    <p className="text-sm text-text-primary mt-1.5 flex items-center gap-1.5">
                      <AlertTriangle
                        className={`h-3.5 w-3.5 ${
                          r.risk === "red" ? "text-danger" : "text-amber-500"
                        }`}
                      />
                      {r.reason}
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-text-muted shrink-0 mt-1" />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
